import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Play, AlertTriangle, CheckCircle, ChevronDown, ChevronRight,
  Loader2, RefreshCw, Code2, Library, History, Shield, Save, Link2, Trash2, Copy, X,
} from 'lucide-react';
import Editor from '@monaco-editor/react';
import { useAuth } from '@/contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

// ── URL share-link helpers (UTF-8 safe base64) ──
function encodeShareCode(code, lang) {
  try {
    const b64 = btoa(unescape(encodeURIComponent(code)));
    return `${window.location.origin}/system-monitor?editor=adhoc&lang=${lang}&code=${encodeURIComponent(b64)}`;
  } catch { return ''; }
}
function decodeShareCode(b64) {
  try { return decodeURIComponent(escape(atob(decodeURIComponent(b64 || '')))); }
  catch { return ''; }
}

const DANGER_BADGE = {
  low: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  medium: 'bg-amber-100 text-amber-800 border-amber-200',
  high: 'bg-red-100 text-red-700 border-red-200',
};

function ago(ts) {
  if (!ts) return '';
  const s = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

// ───────────────────── Result Panel ─────────────────────
function ResultPanel({ result, mode, durationMs, error }) {
  if (!result && !error) return null;
  const r = result?.result || {};
  return (
    <div className="mt-4 border rounded-lg overflow-hidden bg-slate-950 text-slate-100"
         data-testid="script-result-panel">
      <div className="flex items-center justify-between px-4 py-2 bg-slate-900 border-b border-slate-800">
        <div className="flex items-center gap-2 text-xs font-semibold">
          {error ? (
            <><AlertTriangle className="h-3.5 w-3.5 text-red-400" /> <span className="text-red-300">Failed</span></>
          ) : (
            <><CheckCircle className="h-3.5 w-3.5 text-emerald-400" /> <span className="text-emerald-300">OK</span></>
          )}
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
            mode === 'apply' ? 'bg-red-600/30 text-red-200' : 'bg-blue-600/30 text-blue-200'
          }`}>
            {mode === 'apply' ? 'APPLIED' : 'DRY-RUN'}
          </span>
        </div>
        <span className="text-[10px] text-slate-400 font-mono">{durationMs?.toFixed?.(1) ?? durationMs}ms</span>
      </div>
      {error ? (
        <pre className="px-4 py-3 text-xs text-red-300 whitespace-pre-wrap overflow-auto max-h-96"
             data-testid="script-error">{error}</pre>
      ) : (
        <>
          {r.summary && (
            <div className="px-4 py-2 border-b border-slate-800 text-sm">
              <span className="text-slate-400">Summary:</span>{' '}
              <span className="text-emerald-300 font-medium">{r.summary}</span>
              {typeof r.affected === 'number' && (
                <span className="ml-3 text-slate-400">Affected:{' '}
                  <span className="text-amber-300 font-mono">{r.affected}</span>
                </span>
              )}
            </div>
          )}
          {r.logs && r.logs.length > 0 && (
            <div className="px-4 py-2 border-b border-slate-800">
              <p className="text-[10px] text-slate-500 mb-1 uppercase tracking-wide">Logs</p>
              {r.logs.map((line, i) => (
                <div key={i} className="text-xs font-mono text-slate-300">{line}</div>
              ))}
            </div>
          )}
          {r.stdout && (
            <div className="px-4 py-2 border-b border-slate-800">
              <p className="text-[10px] text-slate-500 mb-1 uppercase tracking-wide">stdout</p>
              <pre className="text-xs font-mono text-emerald-200 whitespace-pre-wrap max-h-48 overflow-auto">{r.stdout}</pre>
            </div>
          )}
          {r.stderr && (
            <div className="px-4 py-2 border-b border-slate-800">
              <p className="text-[10px] text-red-400 mb-1 uppercase tracking-wide">stderr</p>
              <pre className="text-xs font-mono text-red-300 whitespace-pre-wrap max-h-48 overflow-auto">{r.stderr}</pre>
            </div>
          )}
          {r.sample && r.sample.length > 0 && (
            <div className="px-4 py-2 border-b border-slate-800">
              <p className="text-[10px] text-slate-500 mb-1 uppercase tracking-wide">
                Sample ({r.sample.length})
              </p>
              <pre className="text-[11px] font-mono text-slate-300 max-h-64 overflow-auto">
                {JSON.stringify(r.sample, null, 2)}
              </pre>
            </div>
          )}
          {r.return !== undefined && r.return !== null && (
            <div className="px-4 py-2">
              <p className="text-[10px] text-slate-500 mb-1 uppercase tracking-wide">Return value</p>
              <pre className="text-[11px] font-mono text-cyan-200 max-h-64 overflow-auto">
                {typeof r.return === 'string' ? r.return : JSON.stringify(r.return, null, 2)}
              </pre>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ───────────────────── Script Library Card ─────────────────────
function ScriptCard({ script, onRun, onDelete, runningId, lastResult }) {
  const isRunning = runningId === script.id;
  const result = lastResult?.[script.id];
  const isCustom = script.source === 'custom';
  const argsSchema = Array.isArray(script.args_schema) ? script.args_schema : [];
  const [argValues, setArgValues] = useState(() =>
    Object.fromEntries(argsSchema.map((f) => [f.name, ''])),
  );

  const validateArgs = () => {
    for (const f of argsSchema) {
      if (f.required && !String(argValues[f.name] || '').trim()) {
        window.alert(`"${f.label || f.name}" is required.`);
        return false;
      }
    }
    return true;
  };

  const handle = async (mode) => {
    if (!validateArgs()) return;
    if (mode === 'apply') {
      const ok = window.confirm(
        `APPLY "${script.title}"?\n\n` +
        `This will WRITE to the database. ` +
        `Make sure you ran Dry-Run first and reviewed the affected count.\n\nContinue?`,
      );
      if (!ok) return;
    }
    onRun(script.id, mode, argsSchema.length ? argValues : undefined);
  };

  const handleDelete = () => {
    if (!window.confirm(`Delete custom script "${script.title}"? This cannot be undone.`)) return;
    onDelete(script.id);
  };

  return (
    <div className="bg-white border rounded-xl p-4 hover:shadow-sm transition-shadow"
         data-testid={`script-card-${script.id}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="text-sm font-semibold text-gray-900">{script.title}</h4>
            {isCustom && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 border border-indigo-200 font-medium">
                custom
              </span>
            )}
            <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${DANGER_BADGE[script.danger] || DANGER_BADGE.low}`}>
              {script.danger}
            </span>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
              {script.category}
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">{script.description || (isCustom ? 'User-saved script' : '')}</p>
          {isCustom && script.created_by && (
            <p className="text-[10px] text-gray-400 mt-1">by {script.created_by} · {ago(script.created_at)}</p>
          )}
        </div>
        {isCustom && (
          <button onClick={handleDelete} title="Delete custom script"
            className="p-1.5 rounded-md text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors"
            data-testid={`script-delete-${script.id}`}>
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {argsSchema.length > 0 && (
        <div className="mt-3 space-y-2 bg-slate-50 border border-slate-200 rounded-lg p-3"
             data-testid={`script-args-${script.id}`}>
          {argsSchema.map((f) => (
            <div key={f.name} className="flex flex-col gap-1">
              <label className="text-[11px] font-medium text-gray-600">
                {f.label || f.name}
                {f.required && <span className="text-red-500 ml-1">*</span>}
              </label>
              <input
                type={f.type === 'number' ? 'number' : 'text'}
                value={argValues[f.name] || ''}
                onChange={(e) => setArgValues((p) => ({ ...p, [f.name]: e.target.value }))}
                placeholder={f.placeholder || ''}
                className="text-xs px-2 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none"
                data-testid={`script-arg-${script.id}-${f.name}`}
              />
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2 mt-3">
        <button
          onClick={() => handle('dry_run')}
          disabled={isRunning}
          className="text-xs px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:opacity-50 font-medium flex items-center gap-1.5"
          data-testid={`script-dry-run-${script.id}`}
        >
          {isRunning ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
          Dry-Run
        </button>
        <button
          onClick={() => handle('apply')}
          disabled={isRunning}
          className="text-xs px-3 py-1.5 rounded-lg bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-50 font-medium flex items-center gap-1.5"
          data-testid={`script-apply-${script.id}`}
        >
          {isRunning ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
          Apply
        </button>
        {result && (
          <span className="text-[10px] text-gray-400 ml-auto">
            last: {result.mode} · {result.result?.result?.affected ?? 0} affected
          </span>
        )}
      </div>
      {result && (
        <ResultPanel result={result.result} mode={result.mode} durationMs={result.result?.duration_ms} />
      )}
    </div>
  );
}

// ───────────────────── Script Library Tab ─────────────────────
function ScriptLibraryTab({ token, refreshKey }) {
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runningId, setRunningId] = useState(null);
  const [results, setResults] = useState({});
  const [error, setError] = useState(null);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchScripts = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/scripts/library`, { headers });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setScripts(d.scripts || []);
      setError(null);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchScripts(); }, [fetchScripts, refreshKey]);

  const runScript = async (id, mode, args) => {
    setRunningId(id);
    try {
      const body = { mode };
      if (args && Object.keys(args).length) body.args = args;
      const r = await fetch(`${API}/api/admin/scripts/library/${id}/run`, {
        method: 'POST', headers, body: JSON.stringify(body),
      });
      const d = await r.json();
      setResults(p => ({ ...p, [id]: { mode, result: d, error: !r.ok ? (d.detail || `HTTP ${r.status}`) : null } }));
    } catch (e) {
      setResults(p => ({ ...p, [id]: { mode, result: null, error: e.message } }));
    } finally {
      setRunningId(null);
    }
  };

  const deleteCustom = async (id) => {
    try {
      const r = await fetch(`${API}/api/admin/scripts/library/custom/${id}`, {
        method: 'DELETE', headers,
      });
      if (r.ok) fetchScripts();
    } catch (e) { console.error(e); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20" data-testid="library-loading">
      <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
    </div>
  );
  if (error) return (
    <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 text-sm" data-testid="library-error">
      {error}
    </div>
  );

  // Group by category
  const byCategory = scripts.reduce((acc, s) => {
    (acc[s.category] = acc[s.category] || []).push(s);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="script-library-tab">
      <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
        <Shield className="h-3.5 w-3.5" />
        <span>
          <strong>Dry-Run first.</strong> All scripts are idempotent. Apply only after reviewing the sample diff.
          Every run is audited.
        </span>
      </div>
      {Object.entries(byCategory).map(([cat, items]) => (
        <div key={cat}>
          <h3 className="text-sm font-semibold text-gray-700 mb-3">{cat}</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {items.map(s => (
              <ScriptCard key={s.id} script={s} onRun={runScript} onDelete={deleteCustom}
                          runningId={runningId} lastResult={results} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ───────────────────── Ad-hoc Editor Tab ─────────────────────
const ADHOC_TEMPLATE = `# Ad-hoc Python — runs server-side against MongoDB (motor).
# Available: 'db' (motor database), print()
# Imports / file IO / dunder access are blocked.
# Writes require 'Apply' mode + confirm.
#
# Examples:
#   n = await db.leads.count_documents({"status": "NEW"})
#   print("NEW leads:", n)
#   return {"count": n}

n = await db.service_leads.count_documents({})
print(f"service_leads total: {n}")
return {"total": n}
`;

function AdhocEditorTab({ token, initialCode, initialLang, onSaved }) {
  const [code, setCode] = useState(initialCode || ADHOC_TEMPLATE);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState('dry_run');
  const [lang, setLang] = useState(initialLang || 'python');
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [shareUrl, setShareUrl] = useState('');

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const run = async (chosenMode) => {
    if (chosenMode === 'apply') {
      const ok = window.confirm(
        'APPLY mode will execute WRITES against the database.\n\n' +
        'Make sure you have run Dry-Run first.\n\nProceed?',
      );
      if (!ok) return;
    }
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      if (lang === 'javascript') {
        // Run JS in-browser. We deliberately do NOT have a server-side JS endpoint.
        const t0 = performance.now();
        let out = [];
        const captured = { log: console.log, error: console.error, warn: console.warn };
        console.log = (...a) => { out.push(a.map(String).join(' ')); captured.log(...a); };
        console.error = (...a) => { out.push('ERR: ' + a.map(String).join(' ')); captured.error(...a); };
        console.warn = (...a) => { out.push('WARN: ' + a.map(String).join(' ')); captured.warn(...a); };
        let ret;
        try {
          // eslint-disable-next-line no-new-func
          const fn = new Function('API', 'token', 'fetch', `return (async () => { ${code} })()`);
          ret = await fn(API, token, fetch);
        } finally {
          console.log = captured.log;
          console.error = captured.error;
          console.warn = captured.warn;
        }
        setResult({
          mode: chosenMode,
          duration_ms: performance.now() - t0,
          result: {
            return: ret === undefined ? null : ret,
            stdout: out.join('\n'),
            stderr: '',
            summary: 'JS executed in-browser',
          },
        });
      } else {
        const r = await fetch(`${API}/api/admin/scripts/adhoc/python`, {
          method: 'POST', headers,
          body: JSON.stringify({ code, mode: chosenMode, timeout_ms: 15000 }),
        });
        const d = await r.json();
        if (!r.ok) {
          setError(typeof d.detail === 'string' ? d.detail : JSON.stringify(d.detail, null, 2));
        } else {
          setResult(d);
        }
      }
      setMode(chosenMode);
    } catch (e) {
      setError(e.message);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="adhoc-editor-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-1 bg-white rounded-lg border p-0.5">
          {['python', 'javascript'].map(l => (
            <button key={l} onClick={() => setLang(l)} data-testid={`lang-${l}`}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                lang === l ? 'bg-slate-800 text-white' : 'text-gray-500 hover:bg-gray-100'
              }`}>
              {l === 'python' ? 'Python (server)' : 'JavaScript (browser)'}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShareUrl(encodeShareCode(code, lang))} disabled={running}
            title="Generate a shareable link that pre-fills the editor"
            className="text-xs px-3 py-1.5 rounded-lg bg-slate-50 text-slate-700 hover:bg-slate-100 border disabled:opacity-50 font-medium flex items-center gap-1.5"
            data-testid="adhoc-share-link">
            <Link2 className="h-3 w-3" /> Share Link
          </button>
          {lang === 'python' && (
            <button onClick={() => setShowSaveModal(true)} disabled={running}
              title="Save this script to the Library for one-click re-run later"
              className="text-xs px-3 py-1.5 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200 disabled:opacity-50 font-medium flex items-center gap-1.5"
              data-testid="adhoc-save-library">
              <Save className="h-3 w-3" /> Save to Library
            </button>
          )}
          <button onClick={() => run('dry_run')} disabled={running}
            className="text-xs px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 font-medium flex items-center gap-1.5"
            data-testid="adhoc-dry-run">
            {running ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
            Dry-Run
          </button>
          {lang === 'python' && (
            <button onClick={() => run('apply')} disabled={running}
              className="text-xs px-3 py-1.5 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 font-medium flex items-center gap-1.5"
              data-testid="adhoc-apply">
              {running ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
              Apply (writes)
            </button>
          )}
        </div>
      </div>

      {shareUrl && (
        <ShareLinkBanner url={shareUrl} onClose={() => setShareUrl('')} />
      )}

      {showSaveModal && (
        <SaveToLibraryModal
          token={token}
          code={code}
          onClose={() => setShowSaveModal(false)}
          onSaved={() => { setShowSaveModal(false); onSaved && onSaved(); }}
        />
      )}

      <div className="border rounded-lg overflow-hidden bg-[#1e1e1e]" data-testid="monaco-editor">
        <Editor
          height="420px"
          language={lang}
          value={code}
          onChange={(v) => setCode(v || '')}
          theme="vs-dark"
          options={{
            minimap: { enabled: false },
            fontSize: 13,
            scrollBeyondLastLine: false,
            wordWrap: 'on',
            tabSize: 4,
            automaticLayout: true,
          }}
        />
      </div>

      {(result || error) && (
        <ResultPanel result={result} mode={mode} durationMs={result?.duration_ms} error={error} />
      )}
    </div>
  );
}

// ───────────────────── Run History Tab ─────────────────────
function RunHistoryTab({ token }) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [kindFilter, setKindFilter] = useState('all');

  const headers = { Authorization: `Bearer ${token}` };

  const fetchRuns = useCallback(async () => {
    setLoading(true);
    try {
      const q = kindFilter === 'all' ? '' : `&kind=${kindFilter}`;
      const r = await fetch(`${API}/api/admin/scripts/runs?limit=100${q}`, { headers });
      const d = await r.json();
      setRuns(d.runs || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [token, kindFilter]);

  useEffect(() => { fetchRuns(); }, [fetchRuns]);

  return (
    <div className="space-y-3" data-testid="run-history-tab">
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1 bg-white rounded-lg border p-0.5">
          {['all', 'library', 'adhoc'].map(k => (
            <button key={k} onClick={() => setKindFilter(k)} data-testid={`runs-filter-${k}`}
              className={`px-3 py-1.5 rounded-md text-xs font-medium capitalize ${
                kindFilter === k ? 'bg-slate-800 text-white' : 'text-gray-500 hover:bg-gray-100'
              }`}>{k}</button>
          ))}
        </div>
        <button onClick={fetchRuns} disabled={loading}
          className="ml-auto text-xs px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 flex items-center gap-1.5"
          data-testid="runs-refresh">
          <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>
      ) : runs.length === 0 ? (
        <div className="text-center py-12 text-gray-400 text-sm bg-white rounded-xl border" data-testid="runs-empty">
          No runs yet. Execute a script to see history here.
        </div>
      ) : (
        <div className="space-y-1.5">
          {runs.map((r, i) => (
            <div key={r.id} className="bg-white border rounded-lg p-3 hover:shadow-sm transition-shadow cursor-pointer"
              onClick={() => setExpanded(expanded === r.id ? null : r.id)}
              data-testid={`run-row-${i}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 flex-wrap min-w-0">
                  {r.ok ? <CheckCircle className="h-3.5 w-3.5 text-emerald-500" /> : <AlertTriangle className="h-3.5 w-3.5 text-red-500" />}
                  <span className="text-xs font-medium text-gray-900 truncate">
                    {r.kind === 'adhoc' ? `Ad-hoc ${r.code_preview ? '(' + r.code_preview.split('\n')[0].slice(0, 40) + '...)' : ''}` : r.script_id}
                  </span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                    r.mode === 'apply' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
                  }`}>{r.mode}</span>
                  {typeof r.affected === 'number' && (
                    <span className="text-[10px] text-gray-500">· {r.affected} affected</span>
                  )}
                </div>
                <div className="flex items-center gap-2 text-[11px] text-gray-500 flex-shrink-0">
                  <span>{r.user_email || r.user_name || 'system'}</span>
                  <span>·</span>
                  <span>{ago(r.timestamp)}</span>
                  <span>·</span>
                  <span className="font-mono">{r.duration_ms}ms</span>
                  {expanded === r.id ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                </div>
              </div>
              {expanded === r.id && (
                <div className="mt-3 pt-3 border-t text-xs space-y-2">
                  {r.summary && <div><span className="text-gray-500">Summary:</span> <span className="text-gray-800">{r.summary}</span></div>}
                  {r.code_preview && (
                    <div>
                      <p className="text-[10px] text-gray-400 mb-0.5 uppercase tracking-wide">Code</p>
                      <pre className="bg-slate-900 text-slate-100 rounded p-2 text-[11px] font-mono max-h-48 overflow-auto">{r.code_preview}</pre>
                    </div>
                  )}
                  {r.error && (
                    <div>
                      <p className="text-[10px] text-red-500 mb-0.5 uppercase tracking-wide">Error</p>
                      <pre className="bg-red-50 text-red-700 rounded p-2 text-[11px] font-mono max-h-48 overflow-auto">{r.error}</pre>
                    </div>
                  )}
                  {r.result && (
                    <div>
                      <p className="text-[10px] text-gray-400 mb-0.5 uppercase tracking-wide">Result</p>
                      <pre className="bg-slate-50 text-slate-800 rounded p-2 text-[11px] font-mono max-h-64 overflow-auto">
                        {JSON.stringify(r.result, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ───────────────────── Share Link Banner ─────────────────────
function ShareLinkBanner({ url, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard may be denied — user can select manually */ }
  };
  return (
    <div className="flex items-center gap-2 bg-slate-900 text-slate-100 rounded-lg px-3 py-2 text-xs"
         data-testid="share-link-banner">
      <Link2 className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
      <input readOnly value={url} className="flex-1 bg-transparent outline-none font-mono text-[11px] truncate"
             onFocus={(e) => e.target.select()} data-testid="share-link-input" />
      <button onClick={copy}
        className="px-2 py-1 rounded bg-slate-700 hover:bg-slate-600 flex items-center gap-1"
        data-testid="share-link-copy">
        {copied ? <CheckCircle className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />}
        {copied ? 'Copied' : 'Copy'}
      </button>
      <button onClick={onClose} className="text-slate-400 hover:text-white"
        data-testid="share-link-close"><X className="h-3.5 w-3.5" /></button>
    </div>
  );
}

// ───────────────────── Save-to-Library Modal ─────────────────────
function SaveToLibraryModal({ token, code, onClose, onSaved }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('Custom');
  const [danger, setDanger] = useState('medium');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async () => {
    if (!title.trim() || title.trim().length < 2) { setErr('Title required (min 2 chars)'); return; }
    setSaving(true);
    setErr(null);
    try {
      const r = await fetch(`${API}/api/admin/scripts/library/custom`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ title, description, category, danger, code }),
      });
      const d = await r.json();
      if (!r.ok) { setErr(typeof d.detail === 'string' ? d.detail : JSON.stringify(d.detail)); return; }
      onSaved && onSaved(d.script);
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}
         data-testid="save-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <Save className="h-4 w-4 text-indigo-600" /> Save to Library
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700" data-testid="save-modal-close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">Title*</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} autoFocus
              placeholder="e.g. Clear stale OTP sessions"
              className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-200"
              data-testid="save-title" />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">Description</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2}
              placeholder="What does this script do? When to run it?"
              className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-200 resize-none"
              data-testid="save-description" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Category</label>
              <input value={category} onChange={(e) => setCategory(e.target.value)}
                className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-200"
                data-testid="save-category" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Danger</label>
              <select value={danger} onChange={(e) => setDanger(e.target.value)}
                className="w-full px-3 py-2 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200"
                data-testid="save-danger">
                <option value="low">low (read-only / safe)</option>
                <option value="medium">medium (writes / idempotent)</option>
                <option value="high">high (destructive)</option>
              </select>
            </div>
          </div>
          {err && <p className="text-xs text-red-600" data-testid="save-error">{err}</p>}
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="text-xs px-3 py-1.5 rounded-lg border hover:bg-gray-50"
            data-testid="save-cancel">Cancel</button>
          <button onClick={submit} disabled={saving}
            className="text-xs px-3 py-1.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1.5"
            data-testid="save-submit">
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
            Save Script
          </button>
        </div>
      </div>
    </div>
  );
}

// ───────────────────── Root Editor Tab ─────────────────────
export default function AdminScriptEditorTab() {
  const { token, user } = useAuth();
  const [tab, setTab] = useState('library');
  const [libRefreshKey, setLibRefreshKey] = useState(0);
  const [initialCode, setInitialCode] = useState(null);
  const [initialLang, setInitialLang] = useState('python');

  // Read URL query params on mount: ?editor=adhoc&code=<base64>&lang=python
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('editor') === 'adhoc') {
      const b64 = params.get('code');
      if (b64) {
        const decoded = decodeShareCode(b64);
        if (decoded) {
          setInitialCode(decoded);
          setInitialLang(params.get('lang') === 'javascript' ? 'javascript' : 'python');
          setTab('adhoc');
        }
      } else {
        setTab('adhoc');
      }
      // Clean URL after consumption so refresh doesn't re-trigger
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const role = user?.role_code || '';
  if (!['CEO', 'ADMIN'].includes(role)) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center" data-testid="editor-forbidden">
        <Shield className="h-8 w-8 mx-auto text-amber-500 mb-2" />
        <p className="text-sm font-semibold text-amber-800">CEO / Admin access required</p>
        <p className="text-xs text-amber-700 mt-1">This editor can read & write to the production database. Restricted by RBAC.</p>
      </div>
    );
  }

  const tabs = [
    { id: 'library', label: 'Script Library', icon: Library },
    { id: 'adhoc', label: 'Ad-hoc Editor', icon: Code2 },
    { id: 'history', label: 'Run History', icon: History },
  ];

  return (
    <div className="space-y-4" data-testid="admin-script-editor">
      <div className="flex gap-1 p-1 bg-gray-100 rounded-lg w-fit">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`editor-tab-${t.id}`}
            className={`px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 transition-colors ${
              tab === t.id ? 'bg-white text-slate-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'
            }`}>
            <t.icon className="h-3.5 w-3.5" /> {t.label}
          </button>
        ))}
      </div>
      {tab === 'library' && <ScriptLibraryTab token={token} refreshKey={libRefreshKey} />}
      {tab === 'adhoc' && (
        <AdhocEditorTab token={token}
          initialCode={initialCode} initialLang={initialLang}
          onSaved={() => { setLibRefreshKey(k => k + 1); setTab('library'); }} />
      )}
      {tab === 'history' && <RunHistoryTab token={token} />}
    </div>
  );
}
